import React, { useState, useEffect } from 'react';
import { Button } from '@cityelectricalfactors/ui-components';

import {
  createBasketJSON,
  displaySuccessfulUpdate,
  uniqueByKey,
} from 'lib/utils';
import { useHTTP } from 'hooks/useRequest';
import {
  addToCartEventsUA,
  addToCartEventsGA4,
} from 'data_layer/ga_add_to_basket_events';
import { ClassicTick } from '../../../assets/common_svgs';
import BackOrderModal from '../../back_order/back_order_modal';
import QuickOrdersSearch from './quick_orders_search';
import QuickOrdersError from './quick_orders_error';

import ConfirmationModal from './confirmation_modal';

/**
 * @param {boolean} props.vatStatus - whether to inc or exc vat
 * @returns the manual entry tab
 */
const QuickOrdersForm = ({ vatStatus }) => {
  const sessionStorage = window.sessionStorage;

  const [basket, setBasket] = useState([]);
  const [inputs, setInputs] = useState([]);
  const [backOrderItems, setBackOrderItems] = useState([]);
  const [orderText, setOrderText] = useState('');
  const [invalidErrors, setInvalidErrors] = useState([]);
  const [removedInputs, setRemovedInputs] = useState([]);
  const [basketButtonDisabled, setBasketButtonDisabled] = useState(true);
  const [fieldsWithNoQty, setFieldsWithNoQty] = useState([]);

  const [clear, setClear] = useState(false);

  const [canSendOrder, setCanSendOrder] = useState(false);
  const [basketJson, setBasketJson] = useState({});

  const [backOrderModal, setBackOrderModal] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);

  const { data: basketUpdateResponse, error } = useHTTP({
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    method: 'put',
    url: canSendOrder ? '/basket' : null,
    data: basketJson,
  });

  /**
   * Method to persist the input rows into session storage
   * @param {Object} productRow - product row data
   */
  const addToSessionStorage = productRow => {
    const currentStoredItems =
      JSON.parse(sessionStorage.getItem('manualEntryItems')) || [];

    currentStoredItems.push(productRow);

    sessionStorage.setItem(
      'manualEntryItems',
      JSON.stringify(uniqueByKey(currentStoredItems, 'id')),
    );
  };

  /**
   * Method to remove the input row/information from session storage
   * @param {Number} rowId - id for row in the list
   */
  const removeFromSessionStorage = rowId => {
    const currentStoredItems =
      JSON.parse(sessionStorage.getItem('manualEntryItems')) || [];

    const updatedList = currentStoredItems.reduce((list, currentStoredItem) => {
      if (currentStoredItem.id !== rowId) {
        list.push(currentStoredItem);
      }
      return list;
    }, []);

    sessionStorage.setItem('manualEntryItems', JSON.stringify(updatedList));
  };

  /**
   * update the basket state and session storage
   * @param {object} basketItem - the new basket object
   */
  const addToOrder = basketItem => {
    setBasket(currBasket => [
      ...currBasket.filter(item => item.id !== basketItem.id),
      basketItem,
    ]);
  };

  /**
   * removal of selected input by adding input id to [removedInputs] array via the passed id
   * @param {number} id - row id
   */
  const removeInput = id => {
    const foundInput = removedInputs.find(({ id: inputId }) => inputId === id);
    if (!foundInput) {
      setRemovedInputs(currInputs => [...currInputs, id]);
    }
  };

  /**
   * removal of id from [removeInputs] via passed id
   * @param {number} id - row id
   */
  const undoInputRemoval = id => {
    setRemovedInputs(removedInputs.filter(inputId => inputId !== id));
  };

  /**
   * Add row id to invalidErrors
   * @param {Object} props.err - type of error as string and id of affected row
   */
  const addInvalidError = id => {
    setInvalidErrors(currErrors => [...currErrors, id]);
  };

  const removeInvalidError = id => {
    const invalidIndex = invalidErrors.indexOf(id);
    if (invalidIndex !== -1) {
      setInvalidErrors(currInvalidErrors =>
        currInvalidErrors.filter(curId => curId !== id),
      );
    }
  };

  const addFieldWithQtyMissing = id => {
    if (fieldsWithNoQty.indexOf(id) === -1) {
      setFieldsWithNoQty(currentFields => [...currentFields, id]);
    }
  };

  const removeFieldWithQtyMissing = id => {
    if (fieldsWithNoQty.indexOf(id) !== -1) {
      setFieldsWithNoQty(currentFields =>
        currentFields.filter(curId => curId !== id),
      );
    }
  };

  /**
   * Remove any order text and add new row
   * @param {number} id - id of current row
   */
  const addNewRow = ({ id }) => {
    // reset order text when input clicked
    if (orderText) {
      setOrderText('');
    }

    const lastInput = inputs[inputs.length - 1];

    if (lastInput.id === id) {
      setInputs(currInputs => [...currInputs, { id: id + 1 }]);
    }
  };

  /**
   * removes an input row and updates session storage
   * @param {number} rowId
   */
  const removeRow = rowId => {
    if (inputs.length > 3) {
      setInputs(currInputs => currInputs.filter(input => input.id !== rowId));
    }

    // update session storage
    removeFromSessionStorage(rowId);
  };

  const removeOrder = id => {
    setBasket(currBasket => currBasket.filter(item => item.id !== id));
  };

  const clearAllOrders = () => {
    setInvalidErrors([]);
    setFieldsWithNoQty([]);
    setInputs([]);
    setBasket([]);
    setOrderText('');
    setRemovedInputs([]);
    setClear(currClear => !currClear);
    setConfirmationModal(false);
    sessionStorage.removeItem('manualEntryItems');
  };

  /**
   * Update basket styles, close back order modal and send order data off
   */
  const addToBasket = () => {
    const basketInfoWrapper = document.querySelector(
      '.basket-preview-price-wrapper',
    );
    basketInfoWrapper.classList.add('is-loading');
    setBasketJson(
      createBasketJSON(
        basket.filter(
          item => item.id !== removedInputs.find(id => item.id === id),
        ),
      ),
    );
    setCanSendOrder(true);
    setBackOrderModal(false);

    const prices = {};
    const gaEventsData = {
      page: 'Quick Order',
      products: basket.map(product => {
        const {
          product_id: productID,
          brand,
          stock_code: id,
          part_code: partCode,
          product_name: itemName,
          primary_root_category: category,
          product_prices: productPrices,
          quantity,
        } = product;

        prices[productID] = productPrices[productID];

        return {
          id: productID,
          stock_code: id,
          part_code: partCode,
          brand_and_description_short: itemName,
          price: productPrices[productID]
            ? productPrices[productID].price
            : productPrices,
          brand,
          category,
          quantity,
          'id-variant': productID,
        };
      }),
      prices,
      sendAll: true,
    };

    // Google Analytics Add to Basket Events
    addToCartEventsUA({ event: 'addBasket', options: gaEventsData });
    addToCartEventsGA4({
      event: 'add_to_cart_ga4',
      options: gaEventsData,
    });
  };

  // check if all used inputs have been removed
  const basketItemsRemoved = removedInputs.length
    ? basket.every(input => removedInputs.includes(input.id))
    : false;

  const checkForBackOrderItems = () => {
    if (backOrderItems.length > 0) {
      setBackOrderModal(true);
    } else {
      addToBasket();
    }
  };

  const isBackOrderItem = ({
    stock_level: itemStockLevel,
    max_order_quantity: itemMaxOrderQty,
    quantity: itemQty,
    total_basket_and_requested_quantity: itemTotalBasketAndRequested,
  }) => {
    /*
    This conditional block checks that the item is suitable to be put on back order

    First part of the conditional makes sure we don't add an item that has a max order qty
    less than the available stock, therefore we check to make sure the
    max order quantity is higher than stock, the item qty is higher than stock and
    the overall requested is higher than stock.

    Second part covers if the item quantity is less than the stock but overall
    requested exceeds the stock

    Thirdly we check if the quantity exceeds stock if the prior two fail

    finally we check if its out of stock entirely
    */

    return (
      (itemMaxOrderQty > itemStockLevel &&
        itemQty > itemStockLevel &&
        itemTotalBasketAndRequested > itemStockLevel) ||
      (itemTotalBasketAndRequested > itemStockLevel &&
        itemStockLevel >= itemQty) ||
      itemQty > itemStockLevel ||
      !itemStockLevel
    );
  };

  useEffect(() => {
    setBackOrderItems([]);
    const items = Object.values(
      basket.filter(
        item => item.id !== removedInputs.find(id => item.id === id),
      ),
    );

    for (let index = 0; index < items.length; index++) {
      const currentItem = items[index];
      if (isBackOrderItem(currentItem)) {
        setBackOrderItems(currentItems => [...currentItems, currentItem]);
      }
    }
  }, [basket, removedInputs]);

  useEffect(() => {
    setCanSendOrder(false);
    if (basketUpdateResponse || error) {
      displaySuccessfulUpdate(basketUpdateResponse);
      setOrderText(
        `${
          basket.filter(
            item => item.id !== removedInputs.find(id => item.id === id),
          ).length
        } ${basket.length > 1 ? 'items' : 'item'}  added to basket`,
      );
      const timer = setTimeout(() => {
        setOrderText('');
      }, 2000);
      setRemovedInputs([]);
      setInputs([]);
      setBasket([]);
      setClear(currClear => !currClear);
      sessionStorage.removeItem('manualEntryItems');
      return () => clearTimeout(timer);
    }
  }, [basketUpdateResponse, error]);

  useEffect(() => {
    const sessionData =
      JSON.parse(sessionStorage.getItem('manualEntryItems')) || [];

    if (sessionData.length) {
      // don't add collection only items to the basket state
      setBasket(
        sessionData.filter(
          d =>
            !Object.keys(d.errors).includes('collection_only') &&
            !Object.keys(d.errors).includes('third_party_shipped'),
        ),
      );

      const sortedData = sessionData.sort((a, b) => a.id - b.id);
      const lastSortedItemId = sortedData[sortedData.length - 1].id;

      setBasketButtonDisabled(false);
      setInputs([
        ...sortedData,
        { id: lastSortedItemId + 1 },
        ...(sortedData.length === 1 ? [{ id: lastSortedItemId + 2 }] : []),
      ]);
    } else {
      setInputs([{ id: 0 }, { id: 1 }, { id: 2 }]);
    }
  }, [clear]);

  return (
    <div className="s-mt-30 mt-30 mb-100">
      <BackOrderModal
        modalIsOpen={backOrderModal}
        closeModal={() => setBackOrderModal(false)}
        successCallback={addToBasket}
        backOrderItems={backOrderItems}
      />
      <ConfirmationModal
        modalIsOpen={confirmationModal}
        closeModal={() => setConfirmationModal(false)}
        clearAllOrders={clearAllOrders}
        content={{
          head: 'Clear Quick Order List',
          text: 'Are you sure you want to clear this list?',
          confirm: 'Confirm',
          close: 'Cancel',
        }}
      />
      <div className="quick-order__row quick-orders__separator fl">
        <div className="quick-orders__title quick-orders__title--stock-and-qty fl">
          <p className="quick-orders__title--stock-code fw-500">
            Stock/Part Code
          </p>
          <p className="quick-orders__title--qty fw-500">Qty</p>
        </div>
        <p className="quick-orders__title quick-orders__title--name quick-orders__title--hide-mobile fw-500">
          Product Name
        </p>
        <div className="quick-orders__title quick-orders__title--delivery-and-price quick-orders__title--hide-mobile m-fl">
          <p className="width-50 fw-500">Stock for delivery</p>
          <p className="width-50 fw-500">Item price</p>
        </div>
        <p className="quick-orders__title--hide-mobile fw-500" />
      </div>
      <div className="mb-30">
        {inputs &&
          inputs.map(product => (
            <QuickOrdersSearch
              addToOrder={newOrder => addToOrder(newOrder)}
              key={product.id.toString()}
              id={product.id}
              vatStatus={vatStatus}
              removeOrder={i => removeOrder(i)}
              removeRow={i => removeRow(i)}
              addNewRow={i => addNewRow(i)}
              addInvalidError={addInvalidError}
              removeInvalidError={removeInvalidError}
              removeInput={removeInput}
              undoInputRemoval={undoInputRemoval}
              setBasketButtonDisabled={setBasketButtonDisabled}
              addFieldWithQtyMissing={addFieldWithQtyMissing}
              removeFieldWithQtyMissing={removeFieldWithQtyMissing}
              productLine={product}
              addToSessionStorage={addToSessionStorage}
              removeFromSessionStorage={removeFromSessionStorage}
            />
          ))}
      </div>
      {orderText && (
        <div className="fl ai-c jc-fs mt-8">
          <ClassicTick height="16" width="16" fillColor="#4AAF4B" />
          <p className="tc-cef-green fs-14 ff-r no-mb ml-3">{orderText}</p>
        </div>
      )}
      <div className="fl fl-col ai-fs m-ai-fe">
        {invalidErrors.length > 0 && (
          <div className="ta-r">
            <QuickOrdersError
              error="One or more of your items have an invalid Stock/Part Code"
              cn="jc-fe ta-l mb-10"
            />
          </div>
        )}
        {fieldsWithNoQty.length > 0 && (
          <div className="ta-r">
            <QuickOrdersError
              error="Please ensure all quantity fields are filled"
              cn="jc-fe ta-l mb-10"
            />
          </div>
        )}
      </div>

      <div
        className={`quick-orders__buttons fl jc-c m-jc-fe ${
          invalidErrors.length === 0 && 'mt-36'
        }`}>
        <Button
          className="quick-orders__clear fs-16 tc-grey-70 b-none"
          label="Clear all orders"
          handleClick={() => setConfirmationModal(true)}>
          Clear Quick Order List
        </Button>
        <Button
          className="fs-16 ml-24 plr-24 fw-500 add-to-basket-btn cef-btn--green"
          label="Add to Basket"
          disabled={
            !basket.length ||
            fieldsWithNoQty.length ||
            invalidErrors.length ||
            basketItemsRemoved ||
            basketButtonDisabled
          }
          handleClick={() => checkForBackOrderItems()}>
          Add to Basket
        </Button>
      </div>
    </div>
  );
};

export default QuickOrdersForm;
